import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Team = () => {
  const [team, setTeam] = useState([]);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const res = await axios.get(`${API}/team`);
        setTeam(res.data);
      } catch (error) {
        console.error('Error fetching team:', error);
      }
    };
    fetchTeam();
  }, []);

  return (
    <div data-testid="team-page">
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <img
          src="/assets/images/provence-dining.jpg"
          alt="Our Team"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 text-center text-white">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-sm tracking-[0.3em] uppercase mb-4"
          >
            Meet The Experts
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-5xl md:text-7xl font-serif"
          >
            Our Team
          </motion.h1>
        </div>
      </section>

      {/* Team Grid */}
      <section className="section-padding bg-bone">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-sm tracking-[0.3em] uppercase text-bronze mb-4">The Pure Style Family</p>
            <h2 className="text-4xl md:text-5xl font-serif mb-6">
              Passionate Designers
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Our talented team brings together decades of experience in interior design, each bringing their unique perspective and expertise to every project.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`team-member-${member.id}`}
                className="group"
              >
                <div className="relative overflow-hidden mb-6">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
                <p className="text-xs tracking-widest uppercase text-bronze mb-2">{member.role}</p>
                <h3 className="text-xl font-serif mb-3">{member.name}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-muted">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-sm tracking-[0.3em] uppercase text-bronze mb-4">What Drives Us</p>
            <h2 className="text-4xl md:text-5xl font-serif">Our Values</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Client-Centered',
                description: 'Your vision is our priority. We listen, understand, and deliver spaces that truly reflect who you are.'
              },
              {
                title: 'Excellence',
                description: 'We hold ourselves to the highest standards, ensuring every detail is thoughtfully considered and beautifully executed.'
              },
              {
                title: 'Collaboration',
                description: 'Great design is a partnership. We work closely with you every step of the way to bring your dreams to life.'
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto flex items-center justify-center border border-bronze text-bronze mb-6">
                  <span className="text-2xl">✦</span>
                </div>
                <h3 className="text-xl font-serif mb-4">{value.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <img
          src="/assets/images/vicenzo-winecellar.jpg"
          alt="Contact Us"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 text-center text-white container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0 }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-4xl md:text-5xl font-serif mb-6">Let's Work Together</h2>
            <p className="text-white/80 mb-10 max-w-xl mx-auto">
              Ready to transform your space? Our team is here to help bring your vision to life.
            </p>
            <Link
              to="/contact"
              data-testid="team-cta-btn"
              className="btn-primary inline-flex items-center gap-2"
            >
              Get In Touch
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
